import React, { Fragment, useState, useCallback, useEffect, FC } from 'react';
import { TextField, Switch, FormControlLabel, IconButton } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { useTranslate } from 'react-admin';
import { useDispatch, useSelector } from 'react-redux';
import debounce from 'lodash/debounce';
import isEmpty from 'lodash/isEmpty';
import cx from 'classnames';
import DeleteIcon from '@material-ui/icons/RemoveCircleOutline';

import { filterChange, selectors, cleanupFilters } from '~/ducks/virtualizedList';
import { AddFilterAction, SelectInput, CurrencyAutocomplete } from '~/components';
import { glTypeLabel, regulatoryTypeLabel } from '~/utils/account';
import { useListFilterStyles } from '~/hooks/useListFilterStyles';
import BackgroundTaskExport from '~/components/BackgroundTaskExport';
import { Account, GlType, RegulatoryType, GL_TYPES, REGULATORY_TYPES } from '~/types';
import { ProfileType } from '~/types/profile';

const resource = 'accounts';

export type AccountListFilters = {
  number?: string;
  type?: string;
  glType?: GlType;
  regulatoryType?: RegulatoryType;
  currency?: string;
  profileType?: ProfileType;
  profileId?: string;
  status?: Account['status'];
  withClosed?: boolean;
  withError?: boolean;
};

const PROFILE_TYPES: ProfileType[] = ['INDIVIDUAL', 'BUSINESS', 'PARTNER', 'TENANT'];

interface Props {
  cleanFilters?: boolean;
}

const label = (key: string) => `resources.accounts.fields.${key}`;

const Filters: FC<Props> = (props) => {
  const { cleanFilters = true } = props;
  const dispatch = useDispatch();
  const translate = useTranslate();
  const t = (key: string) => translate(label(key));
  const listFilterClasses = useListFilterStyles();
  const classes = useStyles();

  const filters = useSelector(selectors.getFilters<AccountListFilters>(resource));
  const [localValues, setLocalValues] = useState<AccountListFilters>(filters);
  const [addedFilters, setAddedFilters] = useState<string[]>([]);

  const [isInitialized, setInitialized] = useState<boolean>(false);

  const filtersAreEmpty = isEmpty(filters);
  useEffect(() => {
    if (!isInitialized) {
      if (cleanFilters || filtersAreEmpty) {
        dispatch(cleanupFilters({ resource }));
        setAddedFilters([]);
      }
      setInitialized(true);
    }
  }, [dispatch, cleanFilters, filtersAreEmpty, isInitialized]);

  useEffect(() => {
    setLocalValues({ ...filters });
  }, [filters]);

  useEffect(() => {
    const activeKeys = Object.keys(filters).filter((k) => !!(filters as any)[k]);
    setAddedFilters((prev) => {
      const merged = new Set([...prev, ...activeKeys]);
      return Array.from(merged);
    });
  }, [filters]);

  const dispatchFilterChange = (name: string, value: any) => {
    dispatch(filterChange({ resource, filters: { [name]: value ?? null } }));
  };

  const debouncedDispatchFilterChange = useCallback(debounce(dispatchFilterChange, 500), []);

  const handleTextInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    if (isInitialized) {
      setLocalValues((prev) => ({ ...prev, [name]: value }));
      debouncedDispatchFilterChange(name, value);
    }
  };

  const handleSelectInput = (e: any) => {
    const { name, value } = e.target;
    setLocalValues((prev) => ({ ...prev, [name]: value }));
    dispatchFilterChange(name, value);
  };

  const hideFilter = (filter: string) => {
    dispatchFilterChange(filter, null);
    setAddedFilters((prev) => prev.filter((f) => f !== filter));
  };

  const allOptionalFilters = [
    'type',
    'glType',
    'regulatoryType',
    'currency',
    'profileType',
    'profileId',
    'status',
  ];

  const renderAddedFilter = (key: string) => {
    switch (key) {
      case 'type':
        return (
          <SelectInput<string>
            label={t('type')}
            name='type'
            value={localValues.type || ''}
            options={[
              { value: 'REGULAR', text: 'REGULAR' },
              { value: 'META', text: 'META' },
              { value: 'SETTLEMENT', text: 'SETTLEMENT' },
            ]}
            onChange={handleSelectInput}
            allowEmpty
            className={cx(listFilterClasses.formControl, listFilterClasses.formControlAdd)}
          />
        );
      case 'glType':
        return (
          <SelectInput<string>
            label={t('glType')}
            name='glType'
            value={localValues.glType || ''}
            options={GL_TYPES.map((code) => ({ value: code, text: translate(glTypeLabel(code)) }))}
            onChange={handleSelectInput}
            allowEmpty
            className={cx(listFilterClasses.formControl, listFilterClasses.formControlAdd)}
          />
        );
      case 'regulatoryType':
        return (
          <SelectInput<string>
            label={t('regulatoryType')}
            name='regulatoryType'
            value={localValues.regulatoryType || ''}
            options={REGULATORY_TYPES.map((code) => ({
              value: code,
              text: translate(regulatoryTypeLabel(code)),
            }))}
            onChange={handleSelectInput}
            allowEmpty
            className={cx(listFilterClasses.formControl, listFilterClasses.formControlAdd)}
          />
        );
      case 'currency':
        return (
          <CurrencyAutocomplete
            label={t('currency')}
            name='currency'
            value={localValues.currency}
            onChange={handleTextInput}
            className={cx(listFilterClasses.formControl, listFilterClasses.formControlAdd)}
          />
        );
      case 'profileType':
        return (
          <SelectInput<string>
            label={t('profileType')}
            name='profileType'
            value={localValues.profileType || ''}
            options={PROFILE_TYPES.map((value) => ({
              value,
              text: translate(`resources.accounts.profileType.${value}`),
            }))}
            onChange={handleSelectInput}
            allowEmpty
            className={cx(listFilterClasses.formControl, listFilterClasses.formControlAdd)}
          />
        );
      case 'profileId':
        return (
          <TextField
            label={t('profileId')}
            name='profileId'
            value={localValues.profileId || ''}
            onChange={handleTextInput}
            className={cx(listFilterClasses.formControl, listFilterClasses.formControlAdd)}
          />
        );
      case 'status':
        return (
          <SelectInput<string>
            label={t('status')}
            name='status'
            value={localValues.status || ''}
            options={[
              { value: 'ACTIVE', text: 'ACTIVE' },
              { value: 'BLOCKED', text: 'BLOCKED' },
              { value: 'CLOSED', text: 'CLOSED' },
            ]}
            onChange={handleSelectInput}
            allowEmpty
            className={cx(listFilterClasses.formControl, listFilterClasses.formControlAdd)}
          />
        );
      default:
        return null;
    }
  };

  const renderAddedFilters = () =>
    addedFilters.map((filter) => {
      const component = renderAddedFilter(filter);
      if (!component) return null;
      return (
        <div className={listFilterClasses.flexContainer} key={filter}>
          {component}
          <IconButton
            aria-label='delete'
            className={listFilterClasses.deleteIcon}
            onClick={() => hideFilter(filter)}
          >
            <DeleteIcon />
          </IconButton>
        </div>
      );
    });

  return (
    <Fragment>
      <div className={listFilterClasses.container}>
        <div className={classes.leftSide}>
          <div className={listFilterClasses.inputsGroup}>
            <TextField
              label={t('number')}
              name='number'
              value={localValues.number || ''}
              onChange={handleTextInput}
              className={cx(listFilterClasses.formControl, listFilterClasses.formControlLong)}
            />
            {renderAddedFilters()}
            <FormControlLabel
              control={
                <Switch
                  checked={!!localValues.withClosed}
                  onChange={(e) => {
                    const val = e.target.checked;
                    setLocalValues((prev) => ({ ...prev, withClosed: val }));
                    dispatchFilterChange('withClosed', val || null);
                  }}
                />
              }
              label={t('withClosed')}
            />
            <FormControlLabel
              control={
                <Switch
                  checked={!!localValues.withError}
                  onChange={(e) => {
                    const val = e.target.checked;
                    setLocalValues((prev) => ({ ...prev, withError: val }));
                    dispatchFilterChange('withError', val || null);
                  }}
                />
              }
              label={t('withError')}
            />
          </div>
        </div>
        <div className={classes.rightSide}>
          <AddFilterAction
            filters={allOptionalFilters
              .filter((f) => !addedFilters.includes(f))
              .sort()
              .map((filterKey) => ({
                key: filterKey,
                label: t(filterKey),
              }))}
            onFilterSelected={(filter: string) => {
              setAddedFilters((prev) => prev.concat([filter]));
            }}
          />
          <BackgroundTaskExport type='ACCOUNT_EXPORT' queryArguments={handleFilters(localValues)} />
        </div>
      </div>
    </Fragment>
  );
};

const handleFilters = (filters: AccountListFilters) => {
  if (!filters) return {};
  return filters;
};

const useStyles = makeStyles(() => ({
  leftSide: {
    maxWidth: 'calc(100% - 150px)',
  },
  rightSide: {
    display: 'flex',
    alignItems: 'flex-end',
    paddingBottom: '8px',
  },
}));

export default Filters;
